import { useCallback, useEffect, useRef, useState } from 'react';
import { streamPlanChat } from './api';
import type { ChatStreamError } from './api';
import type { ChatTurn } from './types';

export interface ChatMessage extends ChatTurn {
  id: number;
}

export interface ChatFailure {
  message: string;
  /** A retry sends the same conversation again; false where waiting is the only answer. */
  retryable: boolean;
}

export interface UsePlanChatStream {
  messages: ChatMessage[];
  streaming: boolean;
  error: ChatFailure | null;
  /** The plan version the latest answer read. Null before the first send. */
  chatVersion: number | null;
  send: (text: string, planVersion: number) => void;
  retry: () => void;
  reset: () => void;
}

// The server's limits (step 8): at most 12 turns, 4000 characters each, 24000 in total,
// ending on a user turn. The oldest history goes first.
const MAX_TURNS = 12;
const MAX_TURN_CHARS = 4000;
const MAX_TOTAL_CHARS = 23000;

export const LATEST_PROMPT = 'Use the latest version of the plan.';

/** The conversation as the server will take it: newest turns kept, starting on a user turn. */
export function toTurns(messages: ChatMessage[]): ChatTurn[] {
  const turns = messages
    .filter((m) => m.content.trim())
    .map<ChatTurn>((m) => ({ role: m.role, content: m.content.slice(0, MAX_TURN_CHARS) }));
  let kept = turns.slice(-MAX_TURNS);
  const total = () => kept.reduce((n, t) => n + t.content.length, 0);
  while (kept.length > 1 && (total() > MAX_TOTAL_CHARS || kept[0].role !== 'user')) kept = kept.slice(1);
  return kept;
}

const failureFor = (e: unknown): ChatFailure => {
  const err = e as ChatStreamError;
  if (err?.status === 429) return { message: 'Too many questions in a minute. Wait a moment, then try again.', retryable: true };
  if (err?.status === 503) return { message: 'The assistant is not set up on this server.', retryable: false };
  if (err?.status === 403) return { message: 'Viewers cannot ask about this plan.', retryable: false };
  return { message: err?.message || 'The assistant could not answer. Try again.', retryable: true };
};

/**
 * The plan chat's conversation, held in memory only: nothing is stored on the server,
 * and a page reload starts over. Tokens are buffered to one render per frame, because
 * React 17 does not batch the state updates a stream reader makes.
 */
export function usePlanChatStream(planId: string): UsePlanChatStream {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState<ChatFailure | null>(null);
  const [chatVersion, setChatVersion] = useState<number | null>(null);
  const seq = useRef(0);
  const abort = useRef<AbortController | null>(null);
  const buffer = useRef('');
  const frame = useRef<number | null>(null);
  // The last request, so Retry can send it again unchanged.
  const lastRequest = useRef<{ history: ChatMessage[]; planVersion: number } | null>(null);

  const flush = useCallback(() => {
    frame.current = null;
    const chunk = buffer.current;
    if (!chunk) return;
    buffer.current = '';
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (!last || last.role !== 'assistant') return prev;
      return [...prev.slice(0, -1), { ...last, content: last.content + chunk }];
    });
  }, []);

  const stopFrame = useCallback(() => {
    if (frame.current !== null) {
      cancelAnimationFrame(frame.current);
      frame.current = null;
    }
  }, []);

  // The newest messages for `send`, which builds history outside a state updater.
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const run = useCallback(
    async (history: ChatMessage[], planVersion: number) => {
      abort.current?.abort();
      stopFrame();
      buffer.current = '';
      const controller = new AbortController();
      abort.current = controller;
      lastRequest.current = { history, planVersion };
      const replyId = ++seq.current;
      setMessages([...history, { id: replyId, role: 'assistant', content: '' }]);
      setError(null);
      setStreaming(true);
      try {
        const { version } = await streamPlanChat(
          planId,
          { turns: toTurns(history), plan_version: planVersion },
          (text) => {
            buffer.current += text;
            if (frame.current === null) frame.current = requestAnimationFrame(flush);
          },
          controller.signal,
        );
        if (controller.signal.aborted) return;
        stopFrame();
        flush();
        setChatVersion(version ?? planVersion);
      } catch (e) {
        if (controller.signal.aborted || (e as Error)?.name === 'AbortError') return;
        stopFrame();
        buffer.current = '';
        // A failed answer leaves no half reply behind: Retry sends the same turns again.
        setMessages((prev) => prev.filter((m) => m.id !== replyId));
        setError(failureFor(e));
      } finally {
        if (abort.current === controller) {
          abort.current = null;
          setStreaming(false);
        }
      }
    },
    [planId, flush, stopFrame],
  );

  const send = useCallback(
    (text: string, planVersion: number) => {
      const content = text.trim();
      if (!content) return;
      const turn: ChatMessage = { id: ++seq.current, role: 'user', content };
      // A reply still streaming is cut where it stands, with its unrendered tail, and kept as history.
      stopFrame();
      const tail = buffer.current;
      buffer.current = '';
      const current = messagesRef.current;
      const last = current[current.length - 1];
      const settled =
        tail && last?.role === 'assistant' ? [...current.slice(0, -1), { ...last, content: last.content + tail }] : current;
      run([...settled.filter((m) => m.content.trim()), turn], planVersion);
    },
    [run, stopFrame],
  );

  const retry = useCallback(() => {
    const last = lastRequest.current;
    if (last) run(last.history, last.planVersion);
  }, [run]);

  const reset = useCallback(() => {
    abort.current?.abort();
    abort.current = null;
    stopFrame();
    buffer.current = '';
    lastRequest.current = null;
    setMessages([]);
    setStreaming(false);
    setError(null);
    setChatVersion(null);
  }, [stopFrame]);

  // A different plan is a different conversation; leaving the page stops the stream.
  useEffect(() => {
    reset();
    return () => {
      const controller = abort.current;
      abort.current = null;
      controller?.abort();
      stopFrame();
    };
  }, [planId, reset, stopFrame]);

  return { messages, streaming, error, chatVersion, send, retry, reset };
}
